import { useCallback, useEffect, useRef, useState } from 'react';

// Pointerdown on any of these (or inside one of them) never starts a pan -
// a card/link/button keeps its own click behaviour, and the orphan panel's
// own grid keeps whatever scrolling it does on its own rather than fighting
// a canvas-wide drag started from inside it.
const PAN_EXCLUDED_SELECTOR = '.org-node, a, button, .org-orphans__grid';

/**
 * useCanvasPan - click-and-drag panning for the org chart canvas.
 *
 * `scrollElRef` is the same scrollable element the zoom hook measures and
 * scrolls (`.org-chart-canvas`) - panning moves its `scrollLeft`/`scrollTop`
 * by the pointer's delta since the last event, so the point of the canvas
 * under the cursor stays under the cursor while dragging, like a native
 * "grab to pan" control.
 *
 * Mouse only, primary button only: a non-mouse `pointerType` (touch, pen)
 * bails out immediately so touch keeps the browser's own native scrolling -
 * a synthetic pan on top of native touch-scroll would double-handle the
 * same gesture. A non-primary `button` (right-click, middle-click) also
 * bails out (round 4, IMPORTANT 2) - among other reasons, a right-click's
 * own context menu can steal pointer capture mid-drag with no pointerup/
 * pointercancel ever following it, which would otherwise leave `isDragging`
 * stuck true forever (`onLostPointerCapture`, below, is the other half of
 * that same fix - it ends the drag whenever capture is lost for any reason,
 * not just the ones this hook itself causes).
 *
 * Returns plain handlers meant to be spread onto the scroll element's own
 * JSX props (`onPointerDown` etc.) - pointer events are not made passive by
 * default the way `wheel`/`touchstart` are, so a normal React synthetic
 * handler is enough here; no native-listener workaround is needed (compare
 * `useOrgChartZoom`'s wheel handling, which does need one).
 */
const useCanvasPan = (scrollElRef) => {
  const [isDragging, setIsDragging] = useState(false);
  // { pointerId, lastX, lastY } while a drag is in progress; null at rest.
  const dragRef = useRef(null);

  const endDrag = useCallback((event) => {
    if (!dragRef.current) return;
    const el = scrollElRef.current;
    if (el && el.releasePointerCapture && event && event.pointerId != null) {
      try {
        el.releasePointerCapture(event.pointerId);
      } catch {
        // Capture may already be gone (e.g. released by a prior
        // pointercancel/lostpointercapture) - nothing left to release.
      }
    }
    dragRef.current = null;
    setIsDragging(false);
  }, [scrollElRef]);

  // A mouse button released, or the window losing focus entirely (e.g.
  // alt-tab), while dragging would otherwise never fire pointerup/
  // pointercancel on this element, leaving `isDragging` stuck true - the
  // cursor stays "grabbing" and nothing is actually held down any more.
  useEffect(() => {
    if (!isDragging) return undefined;
    const handleBlur = () => endDrag();
    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [isDragging, endDrag]);

  const onPointerDown = useCallback((event) => {
    if (event.pointerType !== 'mouse') return;
    // Primary button only - see the hook doc comment above for why.
    if (event.button !== 0) return;
    if (event.target && event.target.closest && event.target.closest(PAN_EXCLUDED_SELECTOR)) return;
    const el = scrollElRef.current;
    if (!el) return;
    // Suppresses the browser's own text-selection gesture starting under
    // the drag (mousedown+move over text is exactly that gesture) - belt-
    // and-suspenders with the `select-none` class the caller applies while
    // `isDragging` (see OrgChart.js, round 4 IMPORTANT 1). Side effect
    // (intentional, round 5): this also suppresses the compatibility
    // `mousedown` event's default action, which includes focus-blurring
    // whatever previously had focus (e.g. the search input) - dragging the
    // canvas background is not itself a focus-changing gesture, so keeping
    // focus where it was is the desired behaviour here, not a regression.
    if (event.preventDefault) event.preventDefault();
    dragRef.current = { pointerId: event.pointerId, lastX: event.clientX, lastY: event.clientY };
    setIsDragging(true);
    if (el.setPointerCapture) {
      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        // Not every environment supports real pointer capture - dragging
        // still works from ordinary bubbling pointermove either way.
      }
    }
  }, [scrollElRef]);

  const onPointerMove = useCallback((event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const el = scrollElRef.current;
    if (!el) return;
    const dx = event.clientX - dragRef.current.lastX;
    const dy = event.clientY - dragRef.current.lastY;
    dragRef.current.lastX = event.clientX;
    dragRef.current.lastY = event.clientY;
    el.scrollLeft -= dx;
    el.scrollTop -= dy;
  }, [scrollElRef]);

  return {
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onPointerLeave: endDrag,
    // Fires whenever this element loses pointer capture for any reason -
    // including a right-click's context menu stealing it mid-drag, or the
    // OS intercepting the gesture - not just the cases this hook's own
    // pointerup/pointercancel handlers cover.
    onLostPointerCapture: endDrag,
  };
};

export default useCanvasPan;
